import React, { useState, useEffect } from 'react';
import {Box, Typography, Grid, CircularProgress, Alert, Button} from '@mui/material';
import StatCard from './dashboard/StatCard';
import HighPriorityIssues from './dashboard/HighPriorityIssues';
import UpcomingVisits from './dashboard/UpcomingVisits';
import PropertyOverview from './dashboard/PropertyOverview';
import {
    FileText,
    Activity,
    Calendar,
    CheckCircle,
    ChevronUp,
    ChevronDown,
    ArrowRight
} from 'react-feather';
import { statusColors } from './mockData';
import { ApiDetails, KEY } from "../../dummyData";
import CryptoJS from "crypto-js";

const TicketDashboard = ({ tickets, openTicketDialog, showAlert }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ticketStats, setTicketStats] = useState({
        total: 0,
        open: 0,
        inProgress: 0,
        scheduled: 0,
        resolved: 0,
        closed: 0,
        highPriority: 0,
        unassigned: 0
    });
    const [propertyStats, setPropertyStats] = useState([]);
    const [upcomingVisits, setUpcomingVisits] = useState([]);
    const [recentlyUpdated, setRecentlyUpdated] = useState([]);
    const [overdueMaintenance, setOverdueMaintenance] = useState([]);

    // Get landlord ID from localStorage
    const getLandlordId = () => {
        try {
            let myMapDetailsDecrypted = CryptoJS.AES.decrypt(
                localStorage.getItem("myMap"),
                KEY
            );
            let originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
            return JSON.parse(originalText)[0][1];
        } catch (error) {
            console.error("Error getting landlord ID:", error);
            setError("Unable to get landlord ID. Please try again.");
            return null;
        }
    };

    // Process dashboard data from tickets prop
    const processDashboardData = (ticketsData) => {
        if (!ticketsData || ticketsData.length === 0) {
            setTicketStats({
                total: 0,
                open: 0,
                inProgress: 0,
                scheduled: 0,
                resolved: 0,
                closed: 0,
                highPriority: 0,
                unassigned: 0
            });
            setPropertyStats([]);
            setUpcomingVisits([]);
            setRecentlyUpdated([]);
            setOverdueMaintenance([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);

            // Handle both API and legacy status formats
            const normalizeStatus = (status) => {
                if (!status) return '';
                status = status.toString().toUpperCase();
                if (status === 'OPEN') return 'Open';
                if (status === 'IN PROGRESS' || status === 'IN_PROGRESS') return 'In Progress';
                if (status === 'SCHEDULED') return 'Scheduled';
                if (status === 'RESOLVED') return 'Resolved';
                if (status === 'CLOSED') return 'Closed';
                return status;
            };

            // Calculate ticket stats
            const stats = {
                total: ticketsData.length,
                open: ticketsData.filter(t => normalizeStatus(t.status) === 'Open').length,
                inProgress: ticketsData.filter(t => normalizeStatus(t.status) === 'In Progress').length,
                scheduled: ticketsData.filter(t => normalizeStatus(t.status) === 'Scheduled').length,
                resolved: ticketsData.filter(t => normalizeStatus(t.status) === 'Resolved').length,
                closed: ticketsData.filter(t => normalizeStatus(t.status) === 'Closed').length,
                highPriority: ticketsData.filter(t =>
                    (t.priority?.toUpperCase() === 'HIGH') &&
                    (normalizeStatus(t.status) === 'Open' || normalizeStatus(t.status) === 'In Progress')
                ).length,
                unassigned: ticketsData.filter(t =>
                    (!t.assignedTo && !t.assigned_to) &&
                    (normalizeStatus(t.status) === 'Open' || normalizeStatus(t.status) === 'In Progress')
                ).length
            };
            setTicketStats(stats);

            // Calculate property stats
            const properties = {};
            ticketsData.forEach(ticket => {
                // Handle various property name formats
                const propertyId = ticket.property_id || ticket.propertyId || 'unknown';
                const propertyName =
                    ticket.property_name ||
                    (ticket.property && ticket.property.name) ||
                    ticket.propertyName ||
                    `Unknown (ID: ${propertyId})`;

                if (!properties[propertyId]) {
                    properties[propertyId] = {
                        id: propertyId,
                        name: propertyName,
                        total: 0,
                        open: 0,
                        inProgress: 0,
                        scheduled: 0,
                        resolved: 0,
                        closed: 0
                    };
                }

                properties[propertyId].total += 1;

                const status = normalizeStatus(ticket.status);
                switch (status) {
                    case 'Open':
                        properties[propertyId].open += 1;
                        break;
                    case 'In Progress':
                        properties[propertyId].inProgress += 1;
                        break;
                    case 'Scheduled':
                        properties[propertyId].scheduled += 1;
                        break;
                    case 'Resolved':
                        properties[propertyId].resolved += 1;
                        break;
                    case 'Closed':
                        properties[propertyId].closed += 1;
                        break;
                    default:
                        break;
                }
            });

            setPropertyStats(Object.values(properties).sort((a, b) => b.total - a.total));

            // Get upcoming scheduled visits
            const now = new Date();
            const scheduled = ticketsData
                .filter(ticket => {
                    const scheduledDate = ticket.scheduled_date || ticket.scheduledDate;
                    if (!scheduledDate) return false;

                    const dateObj = new Date(scheduledDate);
                    if (isNaN(dateObj.getTime())) return false;

                    return dateObj > now &&
                        (normalizeStatus(ticket.status) === 'Scheduled' || normalizeStatus(ticket.status) === 'In Progress');
                })
                .sort((a, b) => {
                    const dateA = new Date(a.scheduled_date || a.scheduledDate);
                    const dateB = new Date(b.scheduled_date || b.scheduledDate);
                    return dateA - dateB;
                })
                .slice(0, 5);

            setUpcomingVisits(scheduled);

            // Get recently updated tickets
            const recent = [...ticketsData]
                .filter(ticket => {
                    const updatedDate = ticket.date_updated || ticket.updatedAt || ticket.date_created || ticket.createdAt;
                    return updatedDate && !isNaN(new Date(updatedDate).getTime());
                })
                .sort((a, b) => {
                    const dateA = new Date(a.date_updated || a.updatedAt || a.date_created || a.createdAt);
                    const dateB = new Date(b.date_updated || b.updatedAt || b.date_created || b.createdAt);
                    return dateB - dateA;
                })
                .slice(0, 5);

            setRecentlyUpdated(recent);

            // Get overdue maintenance (scheduled in past but not resolved)
            const overdue = ticketsData
                .filter(ticket => {
                    const scheduledDate = ticket.scheduled_date || ticket.scheduledDate;
                    if (!scheduledDate) return false;

                    const dateObj = new Date(scheduledDate);
                    if (isNaN(dateObj.getTime())) return false;

                    return dateObj < now &&
                        (normalizeStatus(ticket.status) === 'Scheduled' || normalizeStatus(ticket.status) === 'In Progress');
                })
                .sort((a, b) => {
                    const dateA = new Date(a.scheduled_date || a.scheduledDate);
                    const dateB = new Date(b.scheduled_date || b.scheduledDate);
                    return dateA - dateB;
                });

            setOverdueMaintenance(overdue);

            setError(null);
        } catch (error) {
            console.error("Error processing dashboard data:", error);
            setError("Error processing dashboard data");
        } finally {
            setLoading(false);
        }
    };

    // Fetch dashboard data from API
    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            const landlordId = getLandlordId();
            if (!landlordId) {
                setLoading(false);
                return;
            }

            // Get overall ticket stats
            const ticketsResponse = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    landlord_id: landlordId
                })
            });

            if (ticketsResponse.status === 200) {
                const tickets = await ticketsResponse.json();
                // Process the tickets data - this will also set upcoming visits
                processDashboardData(tickets);
            } else if (ticketsResponse.status === 404) {
                // No tickets found
                processDashboardData([]);
            } else {
                throw new Error(`Server error: ${ticketsResponse.status}`);
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            setError("Failed to load dashboard data. Please try again.");
            showAlert?.("Failed to load dashboard data", "error");
        } finally {
            setLoading(false);
        }
    };

    // Effect for handling data source
    useEffect(() => {
        if (!tickets || tickets.length === 0) {
            fetchDashboardData();
        } else {
            processDashboardData(tickets);
        }
    }, [tickets]);

    // Calculate ticket trend (last 5 days)
    const calculateTicketTrend = () => {
        if (!tickets || tickets.length === 0) return 0;

        const today = new Date();
        const fiveDaysAgo = new Date(today);
        fiveDaysAgo.setDate(today.getDate() - 5);

        const recentTickets = tickets.filter(ticket => {
            const createdDate = new Date(ticket.date_created || ticket.createdAt);
            return !isNaN(createdDate) && createdDate >= fiveDaysAgo;
        });

        return recentTickets.length;
    };

    // Get trend direction
    const getTrendDirection = () => {
        const recentCount = calculateTicketTrend();
        const totalCount = ticketStats.total;

        if (totalCount === 0) return 'stable';

        // Simplified trend calculation
        if (recentCount > totalCount * 0.4) return 'up';
        if (recentCount < totalCount * 0.2) return 'down';
        return 'stable';
    };

    // Get trend icon
    const getTrendIcon = () => {
        const direction = getTrendDirection();
        switch (direction) {
            case 'up': return <ChevronUp size={16} color="#EF4444" />;
            case 'down': return <ChevronDown size={16} color="#10B981" />;
            default: return <ArrowRight size={16} color="#6B7280" />;
        }
    };

    // Calculate average resolution time in days
    const calculateAverageResolutionTime = () => {
        if (!tickets || tickets.length === 0) return 'N/A';

        const resolvedTickets = tickets.filter(ticket => {
            const status = ticket.status?.toUpperCase();
            return status === 'RESOLVED' || status === 'CLOSED';
        });

        if (resolvedTickets.length === 0) return 'N/A';

        let validResolutionCount = 0;
        const totalTime = resolvedTickets.reduce((sum, ticket) => {
            const created = new Date(ticket.date_created || ticket.createdAt);
            const resolved = new Date(ticket.date_updated || ticket.updatedAt);

            if (isNaN(created.getTime()) || isNaN(resolved.getTime())) return sum;

            validResolutionCount++;
            const diffTime = Math.abs(resolved - created);
            const diffDays = diffTime / (1000 * 60 * 60 * 24);
            return sum + diffDays;
        }, 0);

        // Handle case where all date calculations were invalid
        if (validResolutionCount === 0) return 'N/A';

        return (totalTime / validResolutionCount).toFixed(1);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button
                    variant="outlined"
                    onClick={() => tickets ? processDashboardData(tickets) : fetchDashboardData()}
                >
                    Try Again
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    Maintenance Dashboard
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    Overview of all maintenance requests and scheduled visits
                </Typography>
            </Box>

            {/* Stats summary */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Total Tickets"
                        value={ticketStats.total}
                        icon={<FileText size={20} />}
                        iconBgColor="#6366F1"
                        trendIcon={getTrendIcon()}
                        trendText={`${calculateTicketTrend()} new in last 5 days`}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Open Tickets"
                        value={ticketStats.open + ticketStats.inProgress}
                        icon={<Activity size={20} />}
                        iconBgColor="#F59E0B"
                        details={[
                            {
                                color: statusColors.Open?.color || "#D97706",
                                text: `${ticketStats.open} new`
                            },
                            {
                                color: statusColors['In Progress']?.color || "#3B82F6",
                                text: `${ticketStats.inProgress} in progress`
                            }
                        ]}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Scheduled Visits"
                        value={ticketStats.scheduled}
                        icon={<Calendar size={20} />}
                        iconBgColor="#3B82F6"
                        details={[
                            {
                                icon: <Calendar size={14} />,
                                text: `${upcomingVisits.length} upcoming`
                            }
                        ]}
                        chip={overdueMaintenance.length > 0 ?
                            {
                                label: `${overdueMaintenance.length} overdue`,
                                color: '#EF4444',
                                bgColor: '#FEE2E2'
                            } : null
                        }
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Avg. Resolution Time"
                        value={calculateAverageResolutionTime()}
                        suffix="days"
                        icon={<CheckCircle size={20} />}
                        iconBgColor="#10B981"
                        details={[
                            {
                                icon: <CheckCircle size={14} color="#10B981" />,
                                text: `${ticketStats.resolved + ticketStats.closed} resolved tickets`
                            }
                        ]}
                    />
                </Grid>
            </Grid>

            {/* Second row - High priority & upcoming visits */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <HighPriorityIssues
                        tickets={tickets?.filter(ticket => {
                            // Inline status normalization to fix ESLint error
                            const status = ticket.status?.toString().toUpperCase();
                            return ticket.priority?.toUpperCase() === 'HIGH' &&
                                (status === 'OPEN' || status === 'IN PROGRESS' || status === 'IN_PROGRESS');
                        }) || []}
                        count={ticketStats.highPriority}
                        openTicketDialog={openTicketDialog}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <UpcomingVisits
                        visits={upcomingVisits}
                        count={upcomingVisits.length}
                        openTicketDialog={openTicketDialog}
                    />
                </Grid>
            </Grid>

            {/* Property Overview */}
            <PropertyOverview properties={propertyStats} />
        </Box>
    );
};

export default TicketDashboard;